import { base44 } from '@/api/base44Client';
import { cloud } from '@/backend';
import { forceSyncRuns } from '@/lib/forceSync';
import { store } from '@/storage/sqliteStore';

/**
 * Publish the rig's local shift state as a supervisor-visible snapshot.
 * Normal record writes remain local/queued; only this action (or Force Sync)
 * publishes the queued mutations.
 */
export async function endShift(userId) {
  if (!userId) throw new Error('No signed-in user');

  const [sheets, runs, dailyLogs, airCoreRuns, rcRuns, airCoreSamples] = await Promise.all([
    base44.entities.Sheet.list('-updated_date'),
    base44.entities.DrillRun.list(),
    base44.entities.DailyLog.list('-updated_date'),
    base44.entities.AirCoreRun.list(),
    base44.entities.RCRun.list(),
    base44.entities.AirCoreSample.list(),
  ]);

  const activeSheets = (sheets || []).filter((s) => s.status !== 'Completed');
  const snapshot = await base44.entities.ShiftSnapshot.create({
    shift_ended_at: new Date().toISOString(),
    rig_id: sheets?.[0]?.rig_id || null,
    hole_count: sheets?.length || 0,
    active_hole_count: activeSheets.length,
    run_count: runs?.length || 0,
    daily_log_count: dailyLogs?.length || 0,
    air_core_run_count: airCoreRuns?.length || 0,
    rc_run_count: rcRuns?.length || 0,
    air_core_sample_count: airCoreSamples?.length || 0,
    hole_ids: (sheets || []).map((s) => s.name).filter(Boolean),
  });

  // Upload any legacy localStorage run drafts first, then flush the entire
  // persistent SQLite sync queue. The queue is the source of truth for all
  // unsent changes, including the ShiftSnapshot just created.
  await forceSyncRuns();
  const result = cloud.isEnabled()
    ? await cloud.syncNow(userId)
    : { pushed: 0, pending: 0, failed: 0 };

  return { snapshot, ...result };
}
